/**
 * 将日期字符串解析为 Date 对象
 *
 * 关键规则：
 * - 数据库服务器时区为 Asia/Shanghai (UTC+8)
 * - timestamptz 返回的字符串带时区标记（Z 或 +08:00），new Date() 能正确解析
 * - timestamp without time zone 返回的字符串无时区标记，存储的是北京时间
 *   必须减去 8 小时后用 Date.UTC 构造，这样 formatWithBeijingTimezone 加回 8 小时后得到正确时间
 * - 带时区标记的 ISO 字符串（如 ...Z 或 ...+08:00）由 new Date() 正确解析
 */
function parseDate(dateStr: string): Date | null {
  if (!dateStr || typeof dateStr !== 'string') return null;

  const trimmed = dateStr.trim();

  // 1. 带时区标记的 ISO 字符串: 2026-06-20T03:58:15.157Z 或 2026-06-20T03:58:15+08:00
  //    new Date() 能正确解析，直接使用
  if (/\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2})$/i.test(trimmed)) {
    const d = new Date(trimmed);
    if (!isNaN(d.getTime())) return d;
  }

  // 2. 不带时区的时间字符串: 2026-06-20T21:58:15.157 或 2026-06-20 21:58:15.157
  //    数据库 timestamp without time zone 存储的是北京时间(UTC+8)
  //    减去 8 小时后用 Date.UTC 构造为 UTC Date，formatWithBeijingTimezone 再加回 8 小时
  const BEIJING_OFFSET = 8;
  const match = trimmed.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})[T ](\d{1,2}):(\d{1,2}):(\d{1,2})(?:\.(\d+))?$/);
  if (match) {
    const ms = match[7] ? parseInt(match[7].slice(0, 3).padEnd(3, '0'), 10) : 0;
    return new Date(
      Date.UTC(
        parseInt(match[1], 10),
        parseInt(match[2], 10) - 1,
        parseInt(match[3], 10),
        parseInt(match[4], 10) - BEIJING_OFFSET,
        parseInt(match[5], 10),
        parseInt(match[6], 10),
        ms
      )
    );
  }

  // 3. 纯日期格式: 2026-06-20 (视为北京时间日期)
  const dateOnly = trimmed.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (dateOnly) {
    return new Date(
      Date.UTC(
        parseInt(dateOnly[1], 10),
        parseInt(dateOnly[2], 10) - 1,
        parseInt(dateOnly[3], 10),
        -BEIJING_OFFSET
      )
    );
  }

  // 4. 兜底: 尝试 new Date() 解析（适用于其他带时区格式）
  const fallback = new Date(trimmed);
  if (!isNaN(fallback.getTime())) return fallback;

  return null;
}

/** 使用北京时间时区格式化日期 */
function formatWithBeijingTimezone(date: Date, format: 'date' | 'datetime' | 'datetime-seconds'): string {
  const options: Intl.DateTimeFormatOptions = {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };

  if (format === 'date') {
    const parts = new Intl.DateTimeFormat('zh-CN', options).formatToParts(date);
    const year = parts.find(p => p.type === 'year')?.value || '';
    const month = parts.find(p => p.type === 'month')?.value || '';
    const day = parts.find(p => p.type === 'day')?.value || '';
    return `${year}-${month}-${day}`;
  }

  options.hour = '2-digit';
  options.minute = '2-digit';
  if (format === 'datetime-seconds') {
    options.second = '2-digit';
  }
  options.hour12 = false;

  const parts = new Intl.DateTimeFormat('zh-CN', options).formatToParts(date);
  const year = parts.find(p => p.type === 'year')?.value || '';
  const month = parts.find(p => p.type === 'month')?.value || '';
  const day = parts.find(p => p.type === 'day')?.value || '';
  const hour = parts.find(p => p.type === 'hour')?.value || '';
  const minute = parts.find(p => p.type === 'minute')?.value || '';
  const second = parts.find(p => p.type === 'second')?.value || '';

  if (format === 'datetime') {
    return `${year}-${month}-${day} ${hour}:${minute}`;
  }
  return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
}

export function formatDateTime(dateStr: string | undefined | null): string {
  if (!dateStr || dateStr === '-') return '-';
  try {
    const d = parseDate(dateStr);
    if (!d) return '-';
    return formatWithBeijingTimezone(d, 'datetime');
  } catch {
    return '-';
  }
}

export function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr || dateStr === '-') return '-';
  try {
    const d = parseDate(dateStr);
    if (!d) return '-';
    return formatWithBeijingTimezone(d, 'date');
  } catch {
    return '-';
  }
}

export function formatDateTimeWithSeconds(dateStr: string | undefined | null): string {
  if (!dateStr || dateStr === '-') return '-';
  try {
    const d = parseDate(dateStr);
    if (!d) return '-';
    return formatWithBeijingTimezone(d, 'datetime-seconds');
  } catch {
    return '-';
  }
}

export function toLocalDateString(date: Date): string {
  return formatWithBeijingTimezone(date, 'date');
}

export function toLocalDateTimeString(date: Date): string {
  return formatWithBeijingTimezone(date, 'datetime');
}

/** 获取当前北京时间日期 (YYYY-MM-DD) */
export function getBeijingToday(): string {
  return formatWithBeijingTimezone(new Date(), 'date');
}

/**
 * 获取当前北京时间时间戳，格式 "YYYY-MM-DD HH:mm:ss"
 * 用于写入 timestamp without time zone 列（数据库时区为 Asia/Shanghai）
 * 禁止用 new Date().toISOString()（返回 UTC 时间，会被错误当作北京时间）
 */
export function getBeijingTimestamp(): string {
  return formatWithBeijingTimezone(new Date(), 'datetime-seconds');
}

export function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '0秒';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}小时${minutes}分${secs}秒`;
  } else if (minutes > 0) {
    return `${minutes}分${secs}秒`;
  } else {
    return `${secs}秒`;
  }
}

// 保留 extractBeijingTime 供其他模块使用
interface BeijingTimeParts {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
}

export function extractBeijingTime(dateStr: string): BeijingTimeParts | null {
  if (!dateStr || dateStr === '-') return null;
  try {
    const d = parseDate(dateStr);
    if (!d) return null;
    const parts = new Intl.DateTimeFormat('zh-CN', {
      timeZone: 'Asia/Shanghai',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(d);

    return {
      year: parts.find(p => p.type === 'year')?.value || '',
      month: parts.find(p => p.type === 'month')?.value || '',
      day: parts.find(p => p.type === 'day')?.value || '',
      hour: parts.find(p => p.type === 'hour')?.value || '',
      minute: parts.find(p => p.type === 'minute')?.value || '',
      second: parts.find(p => p.type === 'second')?.value || '',
    };
  } catch {
    return null;
  }
}
